import React, { useEffect, useState } from "react";
import { Grid, GridColumn, GridCustomCellProps, GridCustomHeaderCellProps, GridRowDoubleClickEvent, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { DataResult, process } from "@progress/kendo-data-query";
import { useTranslation } from "react-i18next";
import { DATA_ITEM_KEY, ImportRawFileListButton, ResetRawFileSelector, SELECTED_FIELD, SelectionStatistics, UploadRawFilesButton } from "@presentation/components";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { Toolbar } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useFileExplorerStore } from "@presentation/stores/FileExplorerStore";
import { Breadcrumb, BreadcrumbLinkMouseEvent } from "@progress/kendo-react-layout";
import { SelectedState } from "@presentation/stores/FileExplorerStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { RawFileRepository } from "@infrastructure/repositories";
import { useLocation } from "react-router-dom";

const DIA_ANALYSIS_ID = "66666666-6666-6666-6666-666666666666";

type SelectionVisualState = "none" | "partial" | "all";

type FileExplorerItem = {
  id: string;
  fileName: string;
  isFolder: boolean;
  sizeInBytes: number;
  fileCount?: number | null;
};

type SelectedFile = {
  path: string;
  sizeInBytes: number;
};

type ShiftSelectionEvent = React.SyntheticEvent<Element> & {
  shiftKey?: boolean;
  nativeEvent: Event & { shiftKey?: boolean };
};

const SelectionCheckbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (checked: boolean, event: ShiftSelectionEvent) => void;
}> = ({ checked, indeterminate = false, disabled = false, ariaLabel, onChange }) => {
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
      }}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(Boolean(event.value), event.syntheticEvent as ShiftSelectionEvent)}
      />
      {indeterminate && (
        <span
          aria-hidden={true}
          style={{
            position: "absolute",
            width: "8px",
            height: "2px",
            borderRadius: "999px",
            backgroundColor: disabled ? "#9ca3af" : "var(--kendo-color-primary, #0058e9)",
            pointerEvents: "none",
          }}
        />
      )}
    </span>
  );
};

export const FileExplorerGrid: React.FC<{ select: boolean; allowedExtensions?: string[]; onSelectionChange?: (selectedFiles: SelectedFile[]) => void }> = ({ select, allowedExtensions, onSelectionChange }) => {
  const rawFileRepository = useInjection(RawFileRepository);
  const location = useLocation();
  const { t: loc } = useTranslation();
  const { rawFiles, metaState, selectedState, breadcrumbItems, setRawFiles, setSelectedState, setBreadcrumbItems, setMetaState } = useFileExplorerStore();
  const resumeFromStepId = useJobSetupStore((state) => state.CreateJobState.resumeFromStepId);
  const latestFetchRequestId = React.useRef(0);
  const selectionAnchorIdRef = React.useRef<string | null>(null);

  const [dataResult, setDataResult] = useState<DataResult>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFolderId, setLoadingFolderId] = useState<string | null>(null);
  const setJobRawFilesInStore = useJobSetupStore((state) => state.setJobRawFiles);
  const isDiaAnalysisRerun = new URLSearchParams(location.search).has("rerunJobId") && resumeFromStepId === DIA_ANALYSIS_ID;

  const folderSelectionStates = React.useMemo<Record<string, SelectionVisualState>>(() => {
    const selectedDescendantFileCountByPrefix: Record<string, number> = {};

    Object.entries(selectedState).forEach(([id, meta]) => {
      if (meta.isFolder) {
        return;
      }

      const pathSegments = id.split("/").filter(Boolean);
      if (pathSegments.length <= 1) {
        return;
      }

      let prefix = "";
      for (let index = 0; index < pathSegments.length - 1; index++) {
        prefix = prefix ? `${prefix}/${pathSegments[index]}` : pathSegments[index];
        selectedDescendantFileCountByPrefix[prefix] = (selectedDescendantFileCountByPrefix[prefix] ?? 0) + 1;
      }
    });

    return Object.fromEntries(
      rawFiles
        .filter((item) => item.isFolder)
        .map((item) => {
          const normalizedFolderId = item.id.replace(/\/+$/, "");
          const selectedDescendantFileCount = selectedDescendantFileCountByPrefix[normalizedFolderId] ?? 0;
          const totalFileCount = typeof item.fileCount === "number" ? item.fileCount : null;
          const hasExplicitFolderSelection = !!selectedState[item.id]?.isFolder;

          if (selectedDescendantFileCount === 0) {
            return [item.id, hasExplicitFolderSelection && totalFileCount === 0 ? "all" : "none"];
          }

          if (totalFileCount !== null && totalFileCount >= 0) {
            return [item.id, selectedDescendantFileCount >= totalFileCount ? "all" : "partial"];
          }

          return [item.id, hasExplicitFolderSelection ? "all" : "partial"];
        }),
    );
  }, [rawFiles, selectedState]);

  const getItemSelectionState = (item: { id: string; isFolder: boolean }): SelectionVisualState => {
    if (item.isFolder) {
      return folderSelectionStates[item.id] ?? "none";
    }

    return selectedState[item.id] ? "all" : "none";
  };

  const fetchData = async (folder: string) => {
    const requestId = ++latestFetchRequestId.current;
    setLoading(true);
    try {
      if (folder === "home") folder = "";

      const response = await rawFileRepository.getRawFiles(folder);
      if (requestId !== latestFetchRequestId.current) {
        return;
      }

      if (response.list) {
        const allFiles = response.list.map((rawFile) => {
          const isFolder = rawFile.fileName === "";

          // Compute unique id: location/fileName or just location for folders
          const id = isFolder ? rawFile.location || "" : [rawFile.location, rawFile.fileName].filter(Boolean).join("/");

          // Compute display name
          const name = isFolder
            ? rawFile.location?.replace(/\/$/, "").split("/").pop() || ""
            : rawFile.fileName;

          const isDotDFolder = !isFolder && name.endsWith(".d");

          return {
            ...rawFile,
            id,
            isFolder: isFolder && !isDotDFolder, // `.d` folders are files
            fileName: name,
            fileCount: rawFile.fileCount,
            isSelected: false,
          };
        }).filter(file => {
          if (!allowedExtensions || file.isFolder) return true;
          return allowedExtensions.some(ext => file.fileName.endsWith(ext));
        });

        setRawFiles(allFiles);

      }
    } catch (error) {
      if (requestId !== latestFetchRequestId.current) {
        return;
      }

      console.error("Error fetching data:", error);
    } finally {
      if (requestId === latestFetchRequestId.current) {
        setLoading(false);
        setLoadingFolderId(null);
      }
    }
  };

  useEffect(() => {
    const currentFolderId = breadcrumbItems[breadcrumbItems.length - 1]?.id ?? "home";
    void fetchData(currentFolderId);
  }, []);

  const handleBreadcrumbClick = async (event: BreadcrumbLinkMouseEvent) => {
    const clickedItemIndex = breadcrumbItems.findIndex((item) => item.id === event.id);
    const newBreadcrumbs = breadcrumbItems.slice(0, clickedItemIndex + 1);
    const path = event.id === "home" ? "" : (event.id ?? "");
    setMetaState({ ...metaState, skip: 0, filter: undefined });
    await fetchData(path);
    setBreadcrumbItems(newBreadcrumbs);
  };

  useEffect(() => {
    const processedData = process(rawFiles.map((item) => ({
      ...item,
      sizeInGiB: item.sizeInBytes == null ? null : item.sizeInBytes / (1024 * 1024 * 1024),
    })), metaState);
    const dataWithSelection = processedData.data.map((item) => ({
      ...item,
      [SELECTED_FIELD]: getItemSelectionState(item) === "all",
    }));
    setDataResult({ ...processedData, data: dataWithSelection });
  }, [folderSelectionStates, metaState, rawFiles, selectedState]);

  const updateSelectionForItem = async (newSelectedState: SelectedState, selectedItem: FileExplorerItem, isChecked: boolean) => {
    if (isDiaAnalysisRerun && !isChecked) return;

    if (selectedItem.isFolder) {
      if (isChecked) {
        const subItems = await fetchAllFilesRecursively(selectedItem.id + "/");
        subItems.forEach((file) => {
          newSelectedState[file.id] = { path: file.id, sizeInBytes: file.sizeInBytes, isFolder: file.isFolder };
        });
        newSelectedState[selectedItem.id] = { path: selectedItem.id, sizeInBytes: 0, isFolder: true };
      } else {
        const subItems = await fetchAllFilesRecursively(selectedItem.id + "/");
        subItems.forEach((file) => delete newSelectedState[file.id]);
        delete newSelectedState[selectedItem.id];
      }
    } else {
      if (isChecked) {
        newSelectedState[selectedItem.id] = { path: selectedItem.id, sizeInBytes: selectedItem.sizeInBytes, isFolder: false };
      } else {
        delete newSelectedState[selectedItem.id];
      }
    }
  };

  const hasShiftKey = (event: ShiftSelectionEvent) => {
    if (typeof event.shiftKey === "boolean") {
      return event.shiftKey;
    }

    return Boolean(event.nativeEvent?.shiftKey);
  };

  const toggleSelection = async (selectedItem: FileExplorerItem, isChecked: boolean, event?: ShiftSelectionEvent) => {
    if (isDiaAnalysisRerun && !isChecked) return;

    const visibleItems = (dataResult?.data ?? []) as FileExplorerItem[];
    const anchorId = selectionAnchorIdRef.current;
    const isShiftRangeSelection = Boolean(event && hasShiftKey(event) && anchorId);

    const newSelectedState: SelectedState = { ...selectedState };

    if (isShiftRangeSelection) {
      const anchorIndex = visibleItems.findIndex((item) => item.id === anchorId);
      const selectedIndex = visibleItems.findIndex((item) => item.id === selectedItem.id);

      if (anchorIndex >= 0 && selectedIndex >= 0) {
        const [startIndex, endIndex] = anchorIndex < selectedIndex ? [anchorIndex, selectedIndex] : [selectedIndex, anchorIndex];

        for (const item of visibleItems.slice(startIndex, endIndex + 1)) {
          await updateSelectionForItem(newSelectedState, item, isChecked);
        }

        syncSelection(newSelectedState);
        selectionAnchorIdRef.current = selectedItem.id;
        return;
      }
    }

    await updateSelectionForItem(newSelectedState, selectedItem, isChecked);

    syncSelection(newSelectedState);
    selectionAnchorIdRef.current = selectedItem.id;
  };

  const syncSelection = (newSelectedState: SelectedState) => {
    setSelectedState(newSelectedState);
    const jobRawFiles = Object.entries(newSelectedState)
      .filter(([, meta]) => !meta.isFolder)
      .map(([id, meta]) => ({ path: id, sizeInBytes: meta.sizeInBytes }));

    setJobRawFilesInStore(jobRawFiles);

    // Call custom callback if provided
    if (onSelectionChange) {
      const selectedFiles = Object.entries(newSelectedState)
        .filter(([, meta]) => !meta.isFolder)
        .map(([id, meta]) => ({ path: id, sizeInBytes: meta.sizeInBytes }));
      onSelectionChange(selectedFiles);
    }
  };

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setMetaState(event.dataState);
  };

  const navigateToFolder = async (folderId: string, folderName: string) => {
    setLoadingFolderId(folderId);
    setMetaState({ ...metaState, skip: 0, filter: undefined });
    const folderPath = folderId.endsWith("/") ? folderId : folderId + "/";
    await fetchData(folderPath);
    const newItem = {
      id: folderPath,
      text: folderName,
      disabled: false,
    };
    const newBreadcrumbData = breadcrumbItems.some((item) => item.id === newItem.id) ? breadcrumbItems : [...breadcrumbItems, newItem];
    setBreadcrumbItems(newBreadcrumbData);
  }

  const handleRowDoubleClick = async (event: GridRowDoubleClickEvent) => {
    const selectedItem = event.dataItem;
    if (selectedItem.isFolder) {
      await navigateToFolder(selectedItem.id, selectedItem.fileName);
    }
  };

  const handleFolderClick = async (event: React.MouseEvent, item: FileExplorerItem) => {
    event.preventDefault();
    event.stopPropagation();
    if (item.isFolder) {
      await navigateToFolder(item.id, item.fileName);
    }
  }

  const toggleVisibleSelection = async (isChecked: boolean) => {
    if (isDiaAnalysisRerun && !isChecked) return;

    const updatedSelectedState: SelectedState = { ...selectedState };

    for (const item of dataResult?.data ?? []) {
      if (item.isFolder) {
        const subItems = await fetchAllFilesRecursively(item.id + "/");
        if (isChecked) {
          subItems.forEach((file) => {
            updatedSelectedState[file.id] = { path: file.id, sizeInBytes: file.sizeInBytes, isFolder: file.isFolder };
          });
          updatedSelectedState[item.id] = { path: item.id, sizeInBytes: 0, isFolder: true };
        } else {
          subItems.forEach((file) => delete updatedSelectedState[file.id]);
          delete updatedSelectedState[item.id];
        }
      } else {
        if (isChecked) {
          updatedSelectedState[item.id] = { path: item.id, sizeInBytes: item.sizeInBytes, isFolder: false };
        } else {
          delete updatedSelectedState[item.id];
        }
      }
    }

    syncSelection(updatedSelectedState);
  };

  const visibleItems = dataResult?.data ?? [];
  const fullySelectedVisibleItemCount = visibleItems.filter((item) => getItemSelectionState(item) === "all").length;
  const partiallySelectedVisibleItemCount = visibleItems.filter((item) => getItemSelectionState(item) === "partial").length;
  const areAllVisibleItemsSelected = visibleItems.length > 0 && fullySelectedVisibleItemCount === visibleItems.length;
  const areSomeVisibleItemsSelected = fullySelectedVisibleItemCount > 0 || partiallySelectedVisibleItemCount > 0;


  /**
   * Recursively fetches all files under the given folder path.
   * Treats `.d` folders as files (not recursed).
   */
  const fetchAllFilesRecursively = async (folderPath: string): Promise<{ id: string; sizeInBytes: number; isFolder: boolean }[]> => {
    const result: { id: string; sizeInBytes: number; isFolder: boolean }[] = [];

    const fetchFolder = async (currentPath: string) => {
      const response = await rawFileRepository.getRawFiles(currentPath);
      for (const item of response.list ?? []) {
        const fullPath = `${item.location ? item.location + "/" : ""}${item.fileName}`;

        // Case 1: ".d" folder → treat as file
        if (item.fileName.endsWith(".d") && item.fileName !== "") {
          result.push({ id: fullPath, sizeInBytes: item.sizeInBytes, isFolder: false });
        }
        // Case 2: Normal folder → recurse
        else if (item.fileName === "") {
          result.push({
            id: item.location || "",
            sizeInBytes: item.sizeInBytes || 0,
            isFolder: true,
          });
          await fetchFolder((item.location || "") + "/");
        }
        // Case 3: File → add directly
        else {
          result.push({ id: fullPath, sizeInBytes: item.sizeInBytes, isFolder: false });
        }
      }
    };

    await fetchFolder(folderPath);
    return result;
  };

  return (
    <div className="fill-all" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Toolbar className="page-toolbar" style={{ flex: "0 0 auto", height: "50px" }}>
        <ImportRawFileListButton />
        <ResetRawFileSelector />
        <UploadRawFilesButton
          folder={breadcrumbItems[breadcrumbItems.length - 1]?.id === "home" ? "" : (breadcrumbItems[breadcrumbItems.length - 1]?.id || "")}
          onUploadSuccess={() => fetchData(breadcrumbItems[breadcrumbItems.length - 1]?.id === "home" ? "" : (breadcrumbItems[breadcrumbItems.length - 1]?.id || ""))}
        />
        <Breadcrumb data={breadcrumbItems} onItemSelect={handleBreadcrumbClick} size="medium" style={{ height: "50px" }} />
      </Toolbar>

      {select && (
        <div style={{ flex: "0 0 auto" }}>
          {isDiaAnalysisRerun && (
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#fff8e1",
                color: "#8a6d1f",
                borderRadius: "3px",
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Additions here are applied only to the DIA Analysis step for this restart. Removing raw files is disabled.
            </div>
          )}
          <SelectionStatistics />
        </div>
      )}

      <div style={{ flex: "1 1 auto", minHeight: 0 }}>
        <Grid
          style={{ height: "100%" }}
          resizable={true}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          onRowDoubleClick={(event) => handleRowDoubleClick(event)}
          filterable={true}
          sortable={true}
          pageable={{ pageSizes: [10, 20, 50, 100, 500], buttonCount: 5 }}
          data={dataResult?.data || []}
          total={dataResult?.total || 0}
          skip={metaState.skip}
          take={metaState.take}
          {...metaState}
          onDataStateChange={handleDataStateChange}
        >
          {loading && !loadingFolderId && <div className="k-loading-mask"><span className="k-loading-text">Loading...</span><div className="k-loading-image"></div><div className="k-loading-color"></div></div>}
          {select && (
            <GridColumn
              field={SELECTED_FIELD}
              title="Selected"
              width="50px"
              filterable={false}
              sortable={false}
              headerCell={(props: GridCustomHeaderCellProps) => (
                <th
                  {...props.thProps}
                  onClick={props.onClick}
                  style={{ ...(props.thProps?.style ?? {}), textAlign: "center" }}
                >
                  <SelectionCheckbox
                    checked={areAllVisibleItemsSelected}
                    indeterminate={!areAllVisibleItemsSelected && areSomeVisibleItemsSelected}
                    disabled={visibleItems.length === 0 || (isDiaAnalysisRerun && areAllVisibleItemsSelected)}
                    ariaLabel="Select visible files and folders"
                    onChange={(checked) => {
                      selectionAnchorIdRef.current = null;
                      void toggleVisibleSelection(checked);
                    }}
                  />
                </th>
              )}
              cell={(props: GridCustomCellProps) => {
                const selectionState = getItemSelectionState(props.dataItem);

                return (
                  <td
                    {...props.tdProps}
                    style={{ ...(props.tdProps?.style ?? {}), textAlign: "center" }}
                  >
                    <SelectionCheckbox
                      checked={selectionState === "all"}
                      indeterminate={selectionState === "partial"}
                      disabled={isDiaAnalysisRerun && selectionState === "all"}
                      ariaLabel={`Select ${props.dataItem.isFolder ? "folder" : "file"} ${props.dataItem.fileName}`}
                      onChange={(checked, event) => {
                        void toggleSelection(props.dataItem as FileExplorerItem, checked, event);
                      }}
                    />
                  </td>
                );
              }}
            />
          )}
          <GridColumn
            field="fileName"
            title={loc("fileName")}
            width="500px"
            sortable={true}
            cell={(props) => (
              <td>
                {props.dataItem.isFolder ? (
                  <div
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}
                    onClick={(e) => handleFolderClick(e, props.dataItem)}
                    title="Click to navigate"
                  >
                    {loadingFolderId === props.dataItem.id ? (
                      <span className="k-icon k-i-loading" style={{ color: "var(--kendo-color-primary)" }}></span>
                    ) : (
                      <span>📁</span>
                    )}
                    <span> {props.dataItem.fileName}</span>
                  </div>
                ) : (
                  <>
                    <span>📄 </span>
                    {props.dataItem.fileName}
                  </>
                )}
              </td>
            )}
          />
          <GridColumn field="location" title={loc("fileLocation")} width="250px" />
          <GridColumn
            field="fileCount"
            title={loc("fileCount")}
            width="150px"
            filterable={false}
            cell={(props) => (
              <td>
                {props.dataItem.fileName.endsWith(".d") || props.dataItem.fileCount === null || props.dataItem.fileCount === undefined
                  ? "-"
                  : props.dataItem.fileCount}
              </td>
            )}
          />
          <GridColumn field="sizeInGiB" title={loc("fileSizeInGiB")} width="auto" filter="numeric" cell={(props) => <td>{props.dataItem.sizeInGiB?.toFixed(2) ?? "-"}</td>} />
        </Grid>
      </div>
    </div>
  );
};

export default FileExplorerGrid;
